/**
 * AP-37 Detailblatt O 7.4, 9.2 — **Preise übernehmen**: zuerst die Wirkung je
 * Kalkulation (Angebotssumme und DB % alt → neu, aufklappbar je Position), die
 * übersprungenen Zeilen mit Grund und das Häkchen „auch im Artikelstamm
 * speichern". Übernommen wird je Kalkulation mit dem Stand aus der Vorschau —
 * eine inzwischen geänderte Kalkulation meldet sich, die anderen gehen durch (O-17).
 */
import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { applyPrices } from '../../api/calculation-requests.api'
import { fetchCalculationSettings } from '../../api/calculation-settings.api'
import type { ApplyResult, ApplySkipReason } from '../../types/calculation-requests.types'

const GRUND: Record<ApplySkipReason, string> = {
  LOCKED: 'Fassung festgeschrieben — neue Fassung anlegen, dann erneut übernehmen',
  NOT_CURRENT: 'nicht mehr die aktuelle Fassung — neue Fassung anlegen, dann erneut übernehmen',
  NO_RIGHT: 'kein Bearbeitungsrecht an der Kalkulation',
  UNIT_CHANGED: 'Einheit seit der Anfrage geändert',
  DELETED: 'Kostenzeile gelöscht',
  NO_PRICE: 'kein Preis in der Antwort',
  NO_WEIGHT: 'Gewicht je Einheit fehlt — ohne Gewicht lässt sich der Kilopreis nicht übernehmen',
}
const mengeText = (m: { quantity: number; unitCode: string | null }) => `${m.quantity.toLocaleString('de-DE', { maximumFractionDigits: 3 })} ${m.unitCode ?? ''}`.trim()
const prozent = (x: number | null) => (x == null ? '—' : `${x.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`)

export function UebernehmenDialog({
  choices,
  onClose,
  onFertig,
}: {
  choices: { costLineId: string; requestLineId: string }[]
  onClose: () => void
  onFertig: () => void
}) {
  const [vorschau, setVorschau] = useState<ApplyResult | null>(null)
  const [ergebnis, setErgebnis] = useState<ApplyResult | null>(null)
  const [artikel, setArtikel] = useState(false)
  // Vorgabe aus den Einstellungen (O-18, ab Werk aus).
  useEffect(() => {
    fetchCalculationSettings()
      .then((e) => setArtikel(Boolean(e.requestUpdateArticlesDefault)))
      .catch(() => undefined)
  }, [])
  const [auf, setAuf] = useState<Set<string>>(new Set())
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const vorschauLaden = () => {
    setFehler(null)
    setErgebnis(null)
    applyPrices({ choices, dryRun: true })
      .then(setVorschau)
      .catch((e) => setFehler(apiErrorText(e, 'Die Vorschau ließ sich nicht rechnen.')))
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(vorschauLaden, [])

  const uebernehmen = async () => {
    if (!vorschau) return
    setLaeuft(true)
    try {
      const r = await applyPrices({
        choices,
        updateArticles: artikel && vorschau.updateArticlesAllowed,
        stands: vorschau.calculations.map((k) => ({ calculationId: k.calculationId, updatedAt: k.stand })),
      })
      setErgebnis(r)
      // Offen bleiben, wenn es etwas zu lesen gibt: eine veraltete Kalkulation oder ein Artikel ohne Gewicht (O-61).
      if (!r.calculations.some((k) => k.status === 'STALE') && !r.articlesSkipped?.length) onFertig()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Preise ließen sich nicht übernehmen.'))
    } finally {
      setLaeuft(false)
    }
  }

  const liste = ergebnis ?? vorschau
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-lg border border-border p-6 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-primary">Preise übernehmen</h2>
            <p className="text-xs text-muted-foreground">Die gewählten Antworten werden Einkaufspreis, Preiseinheit, Rabatt und Lieferant der Kostenzeilen; die Kalkulationen werden neu gerechnet.</p>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        {fehler && <div className="mb-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
        {!liste ? (
          <p className="text-sm text-muted-foreground">Wirkung wird gerechnet …</p>
        ) : (
          <div className="space-y-3 text-sm">
            {liste.calculations.map((k) => (
              <div key={k.calculationId} className="rounded-md border border-border p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="font-medium">
                    {k.calculationLabel} {k.calculationName}
                  </div>
                  <div className="text-xs text-muted-foreground">{k.lines} Zeile(n)</div>
                </div>
                <div className="mt-1 flex flex-wrap gap-4 tabular-nums">
                  <span>
                    Angebotssumme {geldText(k.priceBefore)} → <span className="font-medium">{geldText(k.priceAfter)}</span>
                  </span>
                  <span>
                    DB {prozent(k.marginPercentBefore)} → <span className="font-medium">{prozent(k.marginPercentAfter)}</span>
                  </span>
                </div>
                {k.conversions?.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-xs">
                    {k.conversions.map((c) => (
                      <li key={c.costLineId} className="tabular-nums">
                        <span className="rounded bg-sky-100 px-1 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">umgestellt</span> {c.name}: {mengeText(c.from)} → {mengeText(c.to)}
                      </li>
                    ))}
                  </ul>
                )}
                {k.status === 'APPLIED' && <div className="mt-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">übernommen</div>}
                {k.status === 'STALE' && (
                  <div className="mt-1 text-xs font-medium text-destructive">
                    Die Kalkulation wurde inzwischen geändert — nicht übernommen.{' '}
                    <button type="button" className="underline" onClick={vorschauLaden}>
                      Vorschau neu laden
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="mt-1 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() =>
                    setAuf((alt) => {
                      const n = new Set(alt)
                      if (n.has(k.calculationId)) n.delete(k.calculationId)
                      else n.add(k.calculationId)
                      return n
                    })
                  }
                >
                  {auf.has(k.calculationId) ? '▾' : '▸'} {k.positions.length} Position(en)
                </button>
                {auf.has(k.calculationId) && (
                  <ul className="ml-4 mt-1 space-y-0.5 text-xs">
                    {k.positions.map((p) => (
                      <li key={p.positionId} className="tabular-nums">
                        {p.label}: EP {geldText(p.unitPriceBefore)} → {geldText(p.unitPriceAfter)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            {liste.skipped.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs dark:border-amber-700 dark:bg-amber-900/30">
                <div className="mb-1 font-medium">Übersprungen ({liste.skipped.length})</div>
                <ul className="space-y-0.5">
                  {liste.skipped.map((s) => (
                    <li key={s.costLineId}>
                      {s.calculation} · {s.name} — {GRUND[s.reason]}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ergebnis && ergebnis.articlesSkipped?.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs dark:border-amber-700 dark:bg-amber-900/30">
                <div className="mb-1 font-medium">Nicht im Artikelstamm gespeichert ({ergebnis.articlesSkipped.length})</div>
                <ul className="space-y-0.5">
                  {ergebnis.articlesSkipped.map((s) => (
                    <li key={s.costLineId}>{s.name} — der Artikel hat kein Gewicht; ein Kilopreis lässt sich nicht in seine Einheit umrechnen</li>
                  ))}
                </ul>
              </div>
            )}
            {vorschau?.updateArticlesAllowed && !ergebnis && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={artikel} onChange={(e) => setArtikel(e.target.checked)} />
                auch im Artikelstamm speichern (Nettopreis als Lieferantenpreis, Vorzugslieferant bleibt)
              </label>
            )}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={ergebnis ? onFertig : onClose}>
            {ergebnis ? 'Schließen' : 'Abbrechen'}
          </button>
          {!ergebnis && (
            <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50" disabled={laeuft || !vorschau?.calculations.length} onClick={() => void uebernehmen()}>
              Preise übernehmen
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
